import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { checkUserAccess } from '../utils/userAccess';
import { useDashboardData } from '../hooks/useDashboardData';
import { getOrderDetails } from '../services/api/orders';
import * as pickerService from '../services/api/picker';
import { HistoryIcon, LocationIcon, ScaleIcon, MessageIcon, MapIcon, PhoneIcon, PlayIcon, DashboardIcon, BellIcon, UserIcon, CheckIcon } from '../components/Icons';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, pickups, logout, loadPickups, isLoading } = useApp();
  const { theme } = useTheme();
  const [orderFilter, setOrderFilter] = useState('PENDING');
  const [sortBy, setSortBy] = useState('Earliest First');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [productNames, setProductNames] = useState({}); // Map of product_id to product_name

  // Get icon colors based on status (consistent across themes)
  const getIconColor = (isActive) => {
    return isActive ? '#3b82f6' : '#6b7280';
  };

  // Fetch product names once (used in Order Details modal)
  useEffect(() => {
    const fetchProductNames = async () => {
      try {
        const result = await pickerService.getProducts();
        if (result.success && Array.isArray(result.data)) {
          const names = {};
          result.data.forEach((p) => {
            if (p?.product_id != null) {
              const id = p.product_id.toString();
              names[id] = p.product_name || p.name || `Product ${id}`;
            }
          });
          setProductNames(names);
        }
      } catch (e) {
        // Non-blocking: modal will fallback to order payload fields
        console.warn('Failed to fetch product names:', e);
      }
    };

    // Only fetch when logged in and we don't already have names
    if (user && Object.keys(productNames).length === 0) {
      fetchProductNames();
    }
  }, [user, productNames]);

  const getProductName = (product) => {
    const productId = product?.product_id?.toString();
    if (productId && productNames[productId]) return productNames[productId];
    if (product?.product?.product_name) return product.product.product_name;
    if (product?.product_name) return product.product_name;
    if (product?.category) return product.category;
    return `Product ${productId || 'Unknown'}`;
  };

  // Fetch dashboard stats from API with intelligent caching
  const { 
    dashboardData, 
    isLoading: isLoadingDashboard, 
    error: dashboardError,
    refresh: refreshDashboard 
  } = useDashboardData({
    autoRefresh: true,
    refreshInterval: 15 * 1000, // Refresh every 15 seconds for real-time updates
    enabled: !!user
  });

  useEffect(() => {
    // Wait for initial load to complete before checking user
    if (isLoading) {
      return;
    }

    if (!user) {
      navigate('/login');
      return;
    }

    const access = checkUserAccess(user);
    
    if (access.errorMessage) {
      // User type is not PICKER or ADMIN, redirect to access denied
      navigate('/access-denied');
      return;
    }

    if (access.shouldShowPending) {
      // User is INACTIVE, redirect to approval pending
      navigate('/approval-pending');
      return;
    }

    if (!access.hasAccess) {
      // User doesn't have access for other reasons
      navigate('/login');
      return;
    }

    // Load pickups when dashboard is active and user is logged in with proper access
    // The loadPickups function now has intelligent caching, so it's safe to call
    if (user && access.hasAccess && loadPickups) {
      // Load pickups (will use cache if valid, otherwise fetch from API)
      loadPickups(false); // false = don't force refresh, use cache if valid
    }
  }, [user, isLoading, navigate, loadPickups]);

  // Real-time polling: Refresh pickups every 15 seconds for new orders
  useEffect(() => {
    if (!user || !loadPickups) return;

    // Initial load
    loadPickups(false);

    // Set up polling interval for real-time updates
    const pollInterval = setInterval(() => {
      // Force refresh to get latest orders
      loadPickups(false); // Will check cache freshness and fetch if needed
    }, 15 * 1000); // Poll every 15 seconds

    // Refresh when user returns to dashboard (page becomes visible)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && loadPickups) {
        // Immediately refresh when page becomes visible
        loadPickups(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, loadPickups]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="dashboard-screen">
        <div className="dashboard-container" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh' 
        }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const access = checkUserAccess(user);
  if (!access.hasAccess || access.errorMessage || access.shouldShowPending) {
    return null;
  }

  const filteredPickups = pickups.filter(pickup => {
    if (orderFilter === 'PENDING') {
      return pickup.status === 'PICKER ASSIGNED';
    } else if (orderFilter === 'PAID') {
      return pickup.status === 'PAID';
    } else if (orderFilter === 'COMPLETED') {
      return pickup.status === 'COMPLETED';
    }
    return false;
  });

  // Sort pickups based on selected sort option
  const sortedPickups = [...filteredPickups].sort((a, b) => {
    if (sortBy === 'Earliest First') {
      // Sort by order ID (lower ID = assigned earlier) or created_at date
      const aId = parseInt(a.id?.toString().replace(/[^0-9]/g, '') || '0');
      const bId = parseInt(b.id?.toString().replace(/[^0-9]/g, '') || '0');
      
      if (aId !== bId) {
        return aId - bId; // Lower ID comes first (assigned earlier)
      }
      
      // Fallback to created_at if IDs are equal or not available
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      return aDate - bDate;
    } else if (sortBy === 'High Volume') {
      // Sort by volume (highest volume first)
      const parseVolume = (volumeStr) => {
        if (!volumeStr) return 0;
        const match = volumeStr.toString().match(/[\d.]+/);
        return match ? parseFloat(match[0]) : 0;
      };
      
      const aVolume = parseVolume(a.volume);
      const bVolume = parseVolume(b.volume);
      return bVolume - aVolume; // Higher volume comes first
    }
    return 0;
  });

  // Use API data for stats, fallback to calculated values if API data not available
  const pendingCount = dashboardData?.pending_orders ?? pickups.filter(p => p.status === 'PICKER ASSIGNED').length;
  const doneCount = dashboardData?.completed_orders ?? pickups.filter(p => p.status === 'COMPLETED').length;
  const totalWeight = dashboardData?.plastic_collected ?? pickups
    .filter(p => p.status === 'COMPLETED')
    .reduce((sum, p) => {
      const weight = parseFloat(p.volume?.replace('~', '').replace('kg', '') || 0);
      return sum + weight;
    }, 0);

  const handleStartPickup = (orderId) => {
    navigate(`/pickup/${orderId}`);
  };

  const handleViewDetails = async (orderId) => {
    setShowOrderModal(true);
    setIsLoadingDetails(true);
    setDetailsError(null);
    setSelectedOrderDetails(null);

    try {
      const result = await getOrderDetails(orderId);
      if (result.success) {
        setSelectedOrderDetails(result.data);
      } else {
        setDetailsError(result.message || 'Failed to load order details');
      }
    } catch (error) {
      setDetailsError('Failed to load order details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrderDetails(null);
    setDetailsError(null);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="dashboard-screen">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <div className="app-icon">
              <img src="/avro-logo-nobg.png" alt="AVRO Recycling" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <h1 className="app-name">AVRO Recycling</h1>
          </div>
          <button className="help-btn" onClick={() => navigate('/account/help-support')}>Help</button>
        </div>

        {/* Inactive Status Banner - shows until admin changes status to ACTIVE */}
        {user.user_status && user.user_status.toUpperCase() !== 'ACTIVE' && user.user_status.toUpperCase() !== 'NOT APPROVED' && (
          <div className="inactive-banner">
            <span className="inactive-banner-icon">⚠️</span>
            <span className="inactive-banner-message">Account inactive. Contact admin to reactivate.</span>
          </div>
        )}

        {/* User Info */}
        <div className="user-info-section">
          <div className="user-profile" onClick={() => navigate('/account')} style={{ cursor: 'pointer' }}>
            <div className="profile-image-wrapper">
              <div className="profile-image">
                {user.image ? (
                  <img src={user.image} alt={user.name} />
                ) : (
                  <div className="profile-placeholder">
                    <UserIcon size={32} color="currentColor" />
                  </div>
                )}
              </div>
              <div className="online-indicator"></div>
            </div>
              <div className="user-details">
                <h2 className="user-greeting">Hello, {user.name}</h2>
                <div className="user-status-row">
                  <span className="user-role-badge">VERIFIED PICKER</span>
                  <span className="user-id">ID {user.id}</span>
                </div>
              </div>
          </div>
          <div className="online-toggle">
            <div className={`toggle-switch ${user.user_status?.toUpperCase() === 'ACTIVE' ? 'active' : 'inactive'}`}>
              <div className="toggle-slider"></div>
            </div>
            <span className={`online-text ${user.user_status?.toUpperCase() === 'ACTIVE' ? '' : 'inactive'}`}>
              {user.user_status?.toUpperCase() === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-section">
          {isLoadingDashboard && !dashboardData ? (
            // Show loading state
            <>
              <div className="stat-card">
                <div className="stat-label">PENDING</div>
                <div className="stat-value">-</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">COMPLETED</div>
                <div className="stat-value">-</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">COLLECTED</div>
                <div className="stat-value">- kg</div>
              </div>
            </>
          ) : (
            <>
              <div className="stat-card">
                <div className="stat-label">PENDING</div>
                <div className="stat-value">{pendingCount}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">COMPLETED</div>
                <div className="stat-value">{doneCount}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">COLLECTED</div>
                <div className="stat-value">{totalWeight} kg</div>
              </div>
            </>
          )}
          {dashboardError && (
            <div className="dashboard-error" style={{ 
              padding: '8px', 
              marginTop: '8px', 
              background: '#fee', 
              color: '#c33',
              borderRadius: '4px',
              fontSize: '12px',
              textAlign: 'center'
            }}>
              {dashboardError} {dashboardData && '(Using cached data)'}
            </div>
          )}
        </div>

        {/* Order Filters */}
        <div className="order-filters">
          <button
            className={`filter-btn ${orderFilter === 'PENDING' ? 'active' : ''}`}
            onClick={() => setOrderFilter('PENDING')}
          >
            PENDING
          </button>
          <button
            className={`filter-btn ${orderFilter === 'PAID' ? 'active' : ''}`}
            onClick={() => setOrderFilter('PAID')}
          >
            PAID
          </button>
          <button
            className={`filter-btn ${orderFilter === 'COMPLETED' ? 'active' : ''}`}
            onClick={() => setOrderFilter('COMPLETED')}
          >
            COMPLETED
          </button>
        </div>

        {/* Assigned Tasks */}
        <div className="pickups-section">
          <div className="section-header">
            <h3 className="section-title">Assigned Tasks</h3>
            <span className="section-date">
              <span className="calendar-icon">📅</span> {new Date().toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              }).toUpperCase()}
            </span>
          </div>

          <div className="sort-buttons">
            <button
              className={`sort-btn ${sortBy === 'Earliest First' ? 'active' : ''}`}
              onClick={() => setSortBy('Earliest First')}
            >
              Earliest First
            </button>
            <button
              className={`sort-btn ${sortBy === 'High Volume' ? 'active' : ''}`}
              onClick={() => setSortBy('High Volume')}
            >
              High Volume
            </button>
          </div>

          <div className="pickup-cards">
            {sortedPickups.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="empty-title">No tasks found</h3>
                <p className="empty-text">
                  {orderFilter === 'PENDING' 
                    ? 'No pending tasks at the moment' 
                    : orderFilter === 'PAID' 
                    ? 'No paid orders yet' 
                    : 'No completed orders yet'}
                </p>
              </div>
            ) : (
              sortedPickups.map((pickup) => {
              // Format order number (e.g., "ORD-492" or "21" -> "ORD-21")
              const formatOrderNumber = (id) => {
                if (!id) return 'ORD-#000';
                const num = id.toString().replace(/[^0-9]/g, '');
                return `ORD-#${num}`;
              };
              
              // Get schedule period from original API data
              const schedulePeriod = pickup.schedule?.period || '';

              return (
                <div
                  key={pickup.id}
                  className={`pickup-card ${pickup.status === 'PICKER ASSIGNED' ? 'active' : ''}`}
                >
                  <div className="pickup-card-header-new">
                    <div className="order-number-display-new">
                      <span className={`order-number-new ${pickup.status === 'PICKER ASSIGNED' ? 'active' : ''}`}>
                        {formatOrderNumber(pickup.id)}
                      </span>
                    </div>
                    {pickup.status === 'PICKER ASSIGNED' ? (
                      <div className="next-badge-new">• NEXT TASK</div>
                    ) : pickup.status === 'PAID' ? (
                      <div className="paid-badge-new">PAID</div>
                    ) : pickup.status === 'COMPLETED' ? (
                      <div className="completed-badge-new">COMPLETED</div>
                    ) : (
                      <div className="scheduled-badge-new">SCHEDULED</div>
                    )}
                  </div>

                  {pickup.type && (
                    <div className="pickup-type">{pickup.type}</div>
                  )}

                  <div className="pickup-details">
                    <div className="detail-item">
                      <span className="detail-icon">
                        <HistoryIcon size={20} color={getIconColor(true)} />
                      </span>
                      <div className="detail-content">
                        <div className="detail-text">
                          {pickup.schedule.dateFull || pickup.schedule.date}
                        </div>
                        {schedulePeriod && (
                          <div className="detail-subtext">
                            {(() => {
                              // Format period in uppercase
                              const periodUpper = schedulePeriod.toUpperCase();
                              // Check if period already contains time (AM/PM)
                              const hasTime = /(AM|PM)/i.test(schedulePeriod);
                              
                              if (hasTime) {
                                // If period already has time, just uppercase it
                                return periodUpper;
                              } else {
                                // Format as "AFTERNOON 2 am - 4am"
                                // Get start and end times, format to lowercase
                                let startTime = pickup.schedule.start || '';
                                let endTime = pickup.schedule.end || '';
                                
                                // Convert to lowercase and format: "2:00 PM" -> "2 am", "4:00 PM" -> "4am"
                                const formatTime = (time, isEnd = false) => {
                                  if (!time) return '';
                                  let formatted = time.toLowerCase();
                                  // Remove :00 if present (e.g., "2:00 pm" -> "2 pm")
                                  formatted = formatted.replace(':00', '');
                                  // For end time, remove space before am/pm (e.g., "4 pm" -> "4pm")
                                  if (isEnd) {
                                    formatted = formatted.replace(/\s+(am|pm)/g, '$1');
                                  }
                                  return formatted;
                                };
                                
                                startTime = formatTime(startTime, false); // "2 am"
                                endTime = formatTime(endTime, true); // "4am"
                                
                                const timeRange = startTime && endTime ? ` ${startTime} - ${endTime}` : '';
                                return `${periodUpper}${timeRange}`;
                              }
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="detail-item">
                      <span className="detail-icon">
                        <LocationIcon size={20} color={getIconColor(true)} />
                      </span>
                      <div className="detail-content">
                        <div className="detail-text">{pickup.location.address}</div>
                      </div>
                    </div>

                    <div className="detail-item">
                      <span className="detail-icon">
                        <ScaleIcon size={20} color={getIconColor(true)} />
                      </span>
                      <div className="detail-content">
                        <div className="detail-text">{pickup.volume} Volume</div>
                        {pickup.items && pickup.items.length > 0 && (
                          <div className="detail-subtext detail-subtext-uppercase">
                            {pickup.items.map((item, idx) => {
                              const productName = item.product?.product_name || item.product_name || '';
                              return idx === 0 ? productName.toUpperCase() : ` & ${productName.toUpperCase()}`;
                            }).join('')}
                          </div>
                        )}
                      </div>
                    </div>

                  {pickup.instructions && (
                    <div className="special-instructions">
                      <span className="detail-icon">
                        <MessageIcon size={20} color="#f59e0b" />
                      </span>
                      <div className="instructions-box">
                        {pickup.instructions}
                      </div>
                    </div>
                  )}
                  </div>

                  {pickup.status === 'PAID' || pickup.status === 'COMPLETED' ? (
                    <div className="pickup-actions">
                      <button
                        className="btn-view-details"
                        onClick={() => handleViewDetails(pickup.id)}
                      >
                        View Details
                      </button>
                    </div>
                  ) : pickup.status === 'PICKER ASSIGNED' ? (
                    <div className="pickup-actions">
                      <button
                        className="btn-start-pickup"
                        onClick={() => handleStartPickup(pickup.id)}
                      >
                        <PlayIcon size={18} color="#ffffff" />
                        Start Pickup
                      </button>
                      <button className="btn-icon">
                        <MapIcon size={20} color={theme === 'dark' ? "#60a5fa" : "#3b82f6"} />
                      </button>
                      <button className="btn-icon">
                        <PhoneIcon size={20} color={theme === 'dark' ? "#60a5fa" : "#3b82f6"} />
                      </button>
                    </div>
                  ) : (
                    <div className="pickup-actions">
                      <button
                        className="btn-start-pickup btn-start-pickup-scheduled"
                        onClick={() => handleStartPickup(pickup.id)}
                      >
                        <PlayIcon size={18} color={theme === 'dark' ? "#60a5fa" : "#3b82f6"} />
                        Start Pickup
                      </button>
                      <button className="btn-icon btn-icon-scheduled">
                        <MapIcon size={20} color={theme === 'dark' ? "#60a5fa" : "#3b82f6"} />
                      </button>
                      <button className="btn-icon btn-icon-scheduled">
                        <PhoneIcon size={20} color={theme === 'dark' ? "#60a5fa" : "#3b82f6"} />
                      </button>
                    </div>
                  )}
                </div>
            );
            })
            )}
          </div>
        </div>

      </div>

      {/* Order Details Modal */}
      {showOrderModal && (
        <div className="order-modal-overlay" onClick={closeOrderModal}>
          <div className="order-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="order-modal-header">
              <h2 className="order-modal-title">Order Details</h2>
              <button className="order-modal-close" onClick={closeOrderModal}>×</button>
            </div>

            <div className="order-modal-body">
              {isLoadingDetails ? (
                <div className="order-modal-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading order details...</p>
                </div>
              ) : detailsError ? (
                <div className="order-modal-error">
                  <p>{detailsError}</p>
                  <button className="btn-retry" onClick={() => handleViewDetails(selectedOrderDetails?.id)}>
                    Retry
                  </button>
                </div>
              ) : selectedOrderDetails ? (
                <div className="order-details-content">
                  {/* Order Status Banner */}
                  <div className={`order-status-banner ${selectedOrderDetails.order_status?.toLowerCase()}`}>
                    <span className="status-label">Status</span>
                    <span className="status-value">{selectedOrderDetails.order_status || 'N/A'}</span>
                  </div>

                  {/* Order Info Section */}
                  <div className="detail-section">
                    <h3 className="section-heading">Order Information</h3>
                    <div className="detail-grid">
                      <div className="detail-row">
                        <span className="detail-label">Order ID</span>
                        <span className="detail-value">#{selectedOrderDetails.order_id}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Status</span>
                        <span className={`detail-value status-badge ${selectedOrderDetails.order_status?.toLowerCase()}`}>
                          {selectedOrderDetails.order_status || 'N/A'}
                        </span>
                      </div>
                      {selectedOrderDetails.schedule_period && (
                        <div className="detail-row">
                          <span className="detail-label">Schedule</span>
                          <span className="detail-value">{selectedOrderDetails.schedule_period}</span>
                        </div>
                      )}
                      {selectedOrderDetails.order_picked_date && (
                        <div className="detail-row">
                          <span className="detail-label">Picked Date</span>
                          <span className="detail-value">{formatDate(selectedOrderDetails.order_picked_date)}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">Created</span>
                        <span className="detail-value">{formatDate(selectedOrderDetails.order_created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Section */}
                  {selectedOrderDetails.summary && (
                    <div className="detail-section">
                      <h3 className="section-heading">Order Summary</h3>
                      <div className="summary-content">
                        {selectedOrderDetails.summary}
                      </div>
                    </div>
                  )}

                  {/* Picker Details Section */}
                  {selectedOrderDetails.picker && (
                    <div className="detail-section">
                      <h3 className="section-heading">Picker Details</h3>
                      <div className="detail-grid">
                        <div className="detail-row">
                          <span className="detail-label">Name</span>
                          <span className="detail-value">{selectedOrderDetails.picker.name || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Phone</span>
                          <span className="detail-value">{selectedOrderDetails.picker.phone_number || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Email</span>
                          <span className="detail-value">{selectedOrderDetails.picker.email || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Status</span>
                          <span className={`detail-value status-badge ${selectedOrderDetails.picker.user_status?.toLowerCase()}`}>
                            {selectedOrderDetails.picker.user_status || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Products Section */}
                  {selectedOrderDetails.products && selectedOrderDetails.products.length > 0 && (
                    <div className="detail-section">
                      <h3 className="section-heading">Products</h3>
                      <div className="products-list">
                        {selectedOrderDetails.products.map((item, index) => (
                          <div key={item.id || index} className="product-item">
                            {item.image && (
                              <div className="product-image">
                                <img src={item.image} alt={getProductName(item)} />
                              </div>
                            )}
                            <div className="product-info">
                              <span className="product-name">
                                {getProductName(item)}
                              </span>
                            </div>
                            <div className="product-details">
                              <div className="product-qty">
                                <span className="qty-label">Quantity:</span>
                                <span className="qty-value">{item.actual_quantity || 0} kg</span>
                              </div>
                              <div className="product-price">
                                <span className="price-label">Actual Price:</span>
                                <span className="price-value">{formatCurrency(item.actual_price)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Order Image Section */}
                  {selectedOrderDetails.order_image && (
                    <div className="detail-section">
                      <h3 className="section-heading">Order Image</h3>
                      <div className="order-image-container">
                        <img 
                          src={selectedOrderDetails.order_image} 
                          alt="Order" 
                          className="order-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Price Summary */}
                  {selectedOrderDetails.products && selectedOrderDetails.products.length > 0 && (
                    <div className="detail-section payment-section">
                      <h3 className="section-heading">Price Summary</h3>
                      <div className="payment-grid">
                        {selectedOrderDetails.products.map((item, index) => (
                          <div key={item.id || index} className="payment-row">
                            <span className="payment-label">{getProductName(item)} ({item.actual_quantity || 0} kg)</span>
                            <span className="payment-value">{formatCurrency(item.actual_price)}</span>
                          </div>
                        ))}
                        <div className="payment-row total">
                          <span className="payment-label">Total Amount</span>
                          <span className="payment-value">
                            {formatCurrency(
                              selectedOrderDetails.products.reduce((sum, item) => 
                                sum + parseFloat(item.actual_price || 0), 0
                              )
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* User Note */}
                  {selectedOrderDetails.user_note && (
                    <div className="detail-section">
                      <h3 className="section-heading">Customer Note</h3>
                      <div className="instructions-content">
                        {selectedOrderDetails.user_note}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="order-modal-footer">
              <button className="btn-close-modal" onClick={closeOrderModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

